import { handleTrackers } from './creative';
import { Tag } from './tag';
import { paramsToQs } from './utils/object';

import { buster } from './utils/util';

const MRC_LARGE_FORMAT_THRESHOLD = 242500;
/**
 * Delete the specified function from the global scope
 * @param {String} callbackName Name of the callback function from addCallback()
 */
export const removeGlobalCallback = (
  globalScope: Window,
  callbackName: string,
) => {
  try {
    delete globalScope[callbackName as keyof Window];
  } catch (x) {}
};

/**
 * Add a uniquely named callback function to the global scope and return that name.
 * @param {Function} fn Function to call
 * @returns {String} Name of the function
 */
export const addGlobalCallback = <T>(
  win: Window,
  fn: (...result: T[]) => void,
) => {
  var callbackFunction = 'mobkoi_' + buster();
  // @ts-ignore
  win[callbackFunction] = function (...args: T[]) {
    removeGlobalCallback(win, callbackFunction);
    fn.apply(null, args);
  };

  return callbackFunction;
};

type TagUrlOptions = {
  tagId: string;
  params: Record<string, unknown>;
  endpoint: string;
  tagCallback: string;
};

export const makeTagUrl = ({
  tagId,
  params,
  endpoint,
  tagCallback,
}: TagUrlOptions) =>
  `https://${endpoint}/tag/${tagId}/?callback=${tagCallback}&${paramsToQs({
    buster: buster(),
    ...params,
  })}`;

type LogUrlOptions = {
  tagId: string;
  endpoint: string;
  params: Record<string, unknown>;
};

export const makeLogUrl = ({ tagId, endpoint, params }: LogUrlOptions) =>
  `https://${endpoint}/log/${tagId}/?${paramsToQs({
    buster: buster(),
    ...params,
  })}`;

export const beaconImage = (src: string) => {
  new Image().src = src;
};

export const beacon = (url: string) => {
  //  use sendBeacon if avail
  //  if sendBeacon fails (quota used up) fall back to Image
  if (!navigator.sendBeacon || !navigator.sendBeacon(url)) {
    beaconImage(url);
  }
};

export const infoLog = (message: string) => console.info(message);

export const dropTrackers = (tag: Tag) => {
  const trackers = handleTrackers(tag);
  const container = tag.creativeContainer!;

  trackers.forEach((tracker) => {
    if (tracker.type === 'pixel') {
      beaconImage(tracker.tracker);
    }

    if (tracker.type === 'script') {
      container.appendChild(
        Object.assign(document.createElement('script'), {
          type: 'text/javascript',
          async: 1,
          src: tracker.tracker,
        }),
      );
    }

    if (tracker.type === 'impression_exposure') {
      let accumulatedTime = 0;
      let timerStart: number | null = null;
      let hasFired = false;
      let rafId: number | null = null;
      let isLoopActive = false;
      const rect = container.getBoundingClientRect();
      const defaultSize = { h: rect.height, w: rect.width };

      const [{ h, w } = defaultSize] = tag.payload?.creative?.sizes ?? [];
      // calculate the pixels and compare to the large format threshold
      let isLargeFormat = w * h >= MRC_LARGE_FORMAT_THRESHOLD;

      //for large formats we want to trigger at 30% visibility and 50% for smaller formats
      const intersectionRatio = isLargeFormat ? 0.3 : 0.5;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (hasFired) return;

          const isVisible =
            entry.intersectionRatio >= intersectionRatio &&
            entry.isIntersecting;
          if (isVisible && !isLoopActive) {
            isLoopActive = true;
            timerStart = performance.now();

            const step = (now: number) => {
              if (!timerStart || hasFired) return;

              const delta = now - timerStart;
              accumulatedTime += delta;

              if (accumulatedTime >= 3000) {
                beaconImage(tracker.tracker);
                hasFired = true;
                observer.disconnect();
                return;
              }

              timerStart = now;
              rafId = requestAnimationFrame(step);
            };

            rafId = requestAnimationFrame(step);
          } else if (!isVisible && isLoopActive) {
            if (rafId) {
              cancelAnimationFrame(rafId);
              rafId = null;
            }
            timerStart = null;
            isLoopActive = false;
          }
        },
        { threshold: [0.3, 0.5] },
      );

      observer.observe(container);
    }
  });
};
